import { AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import SortableTableHeader from "@/components/ui/sortable-table-header.jsx";
import ClassificationSearch from "@/components/downtime-classification/classification-search.jsx";
import VendorColumnFilter from "@/components/downtime-classification/vendor-column-filter.jsx";
import { cardStyles } from "@/lib/status-styles.js";
import { cn } from "@/lib/utils.js";

/**
 * @typedef {Object} ClassificationColumn
 * @property {string} key - Sort / identity key (e.g. Vendor, Message, IsMech)
 * @property {string} label - Header label
 * @property {boolean} [sortable=true]
 * @property {boolean} [alignRight]
 * @property {boolean} [vendorFilter] - Show the Excel-style vendor filter beside this header
 */

/**
 * Body row classes shared by classification grids (matches Fleet Overview).
 * @returns {string}
 */
function getClassificationStripeClass() {
  return cardStyles.bodyRow;
}

/**
 * Shared shell for Needs Mapping / Your Mappings grids:
 * search header, loading/error/empty states, and table frame with column headers.
 * @param {Object} props
 * @param {string} props.search
 * @param {(value: string) => void} props.onSearchChange
 * @param {string} [props.searchTestId]
 * @param {React.ReactNode} [props.headerActions] - Optional right-side header content (e.g. Save)
 * @param {boolean} props.isLoading
 * @param {string} props.loadingMessage
 * @param {string|null} props.error
 * @param {boolean} props.isEmpty - True when the source dataset has no rows
 * @param {string} props.emptyMessage
 * @param {string} [props.noMatchMessage="No matching downtime reasons"]
 * @param {ClassificationColumn[]} props.columns
 * @param {string} props.sortColumn
 * @param {'asc'|'desc'} props.sortDirection
 * @param {(column: string) => void} props.onSort
 * @param {string[]} props.vendorOptions
 * @param {string[]|null} props.selectedVendors
 * @param {(vendors: string[]|null) => void} props.onVendorsChange
 * @param {object[]} props.filteredAndSortedRows
 * @param {(row: object, index: number) => React.ReactNode} props.renderRow - Table cell content for a row
 * @param {(row: object) => string} props.getRowKey
 * @returns {JSX.Element}
 */
export default function ClassificationGridShell({
  search,
  onSearchChange,
  searchTestId = "input-search-classification",
  headerActions = null,
  isLoading,
  loadingMessage,
  error,
  isEmpty,
  emptyMessage,
  noMatchMessage = "No matching downtime reasons",
  columns,
  sortColumn,
  sortDirection,
  onSort,
  vendorOptions,
  selectedVendors,
  onVendorsChange,
  filteredAndSortedRows,
  renderRow,
  getRowKey,
}) {
  const renderBody = () => {
    if (isLoading) {
      return (
        <div className='flex items-center justify-center py-12'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
          <span className='ml-3 text-muted-foreground'>{loadingMessage}</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <AlertTriangle className='h-8 w-8 text-red-500 mx-auto mb-2' />
            <p className='text-red-600'>{error}</p>
          </div>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className='flex items-center justify-center py-12'>
          <p className='text-muted-foreground'>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className={cardStyles.tableContainer}>
        <Table className='w-full'>
          <TableHeader className={cardStyles.tableHeader}>
            <TableRow className={cardStyles.tableRow}>
              {columns.map((column) => {
                const headClass = cn(
                  cardStyles.tableHead,
                  "py-1.5",
                  column.alignRight && "text-right"
                );

                const label =
                  column.sortable !== false ? (
                    <SortableTableHeader
                      column={column.key}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={() => onSort(column.key)}
                      className={column.alignRight ? "justify-end" : undefined}>
                      {column.label}
                    </SortableTableHeader>
                  ) : (
                    column.label
                  );

                return (
                  <TableHead key={column.key} className={headClass}>
                    {column.vendorFilter ? (
                      <div className='flex items-center gap-1'>
                        {label}
                        <VendorColumnFilter
                          vendors={vendorOptions}
                          selectedVendors={selectedVendors}
                          onChange={onVendorsChange}
                        />
                      </div>
                    ) : (
                      label
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='py-12 text-center text-muted-foreground'>
                  {noMatchMessage}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedRows.map((row, index) => (
                <TableRow
                  key={getRowKey(row)}
                  className={getClassificationStripeClass(index)}>
                  {renderRow(row, index)}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div>
      <div
        className={cn(
          cardStyles.header,
          headerActions ? "justify-between" : "justify-start"
        )}>
        <ClassificationSearch
          value={search}
          onChange={onSearchChange}
          placeholder='Search vendor or description'
          testId={searchTestId}
        />
        {headerActions}
      </div>
      {renderBody()}
    </div>
  );
}
